import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import nationalities from "./nationalities.json";

const DIAL_CODES = [
  { code: "MY", dial: "+60" },
  { code: "SG", dial: "+65" },
  { code: "PH", dial: "+63" },
  { code: "ID", dial: "+62" },
  { code: "TH", dial: "+66" },
  { code: "VN", dial: "+84" },
  { code: "CN", dial: "+86" },
  { code: "HK", dial: "+852" },
  { code: "TW", dial: "+886" },
  { code: "IN", dial: "+91" },
  { code: "PK", dial: "+92" },
  { code: "BD", dial: "+880" },
  { code: "JP", dial: "+81" },
  { code: "KR", dial: "+82" },
  { code: "AU", dial: "+61" },
  { code: "NZ", dial: "+64" },
  { code: "GB", dial: "+44" },
  { code: "US", dial: "+1" },
  { code: "CA", dial: "+1" },
  { code: "RU", dial: "+7" },
  { code: "KZ", dial: "+7" },
  { code: "UA", dial: "+380" },
  { code: "BY", dial: "+375" },
  { code: "DE", dial: "+49" },
  { code: "FR", dial: "+33" },
  { code: "IT", dial: "+39" },
  { code: "ES", dial: "+34" },
  { code: "NL", dial: "+31" },
  { code: "TR", dial: "+90" },
  { code: "AE", dial: "+971" },
  { code: "SA", dial: "+966" },
  { code: "QA", dial: "+974" },
  { code: "BH", dial: "+973" },
  { code: "KW", dial: "+965" },
  { code: "OM", dial: "+968" },
  { code: "EG", dial: "+20" },
  { code: "ZA", dial: "+27" },
  { code: "BR", dial: "+55" },
];

const PRODUCT_NAMES = {
  "standard-visit": "PETRONAS Twin Towers Sky Xperience",
  "evening-xperience": "Evening Xperience",
  "priority-access": "Priority Access",
  "hop-on-hop-off": "Sky Xperience + KL Hop-On Hop-Off",
  "symphony-xperience": "Sky Xperience - Symphony Xperience",
  "cultural-feast": "Sky Xperience + Malaysia Cultural Feast",
  "petrosains-double-deal": "Sky Xperience + Petrosains",
};

function readStored(key, storage = localStorage) {
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function normalizeTickets(value) {
  if (!Array.isArray(value)) return [];

  return value
    .filter((ticket) => ticket && typeof ticket === "object")
    .map((ticket, index) => ({
      ...ticket,
      id: ticket.id || `ticket-${index}`,
      quantity: Math.floor(Number(ticket.quantity)),
      price: Number(ticket.price),
    }))
    .filter(
      (ticket) =>
        Number.isFinite(ticket.quantity) &&
        ticket.quantity > 0 &&
        Number.isFinite(ticket.price) &&
        ticket.price >= 0
    );
}

function parseDate(value) {
  const match =
    typeof value === "string" &&
    /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return date.getFullYear() === Number(match[1]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[3])
    ? date
    : null;
}

function formatDate(value) {
  return (
    parseDate(value)?.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) || ""
  );
}

function formatTime(value) {
  if (typeof value !== "string") return "";

  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return value;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (hour > 23 || minute > 59) return "";

  return `${String(hour % 12 || 12).padStart(2, "0")}:${String(
    minute
  ).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
}

function formatSession(date, time) {
  return [formatDate(date), formatTime(time)]
    .filter(Boolean)
    .join(", ");
}

function money(value) {
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function flag(code) {
  return code
    .toUpperCase()
    .split("")
    .map((letter) =>
      String.fromCodePoint(127397 + letter.charCodeAt(0))
    )
    .join("");
}

function ticketStatus(ticket, booking) {
  const status =
    ticket.dateStatus ||
    (ticket.visitDate === booking?.visitDate
      ? booking?.dateStatus
      : null);

  if (status === "peak") return "Peak";
  if (status === "offPeak") return "Non-Peak";
  if (status === "publicHoliday") return "Public Holiday";

  const date = parseDate(ticket.visitDate);

  return date && [0, 6].includes(date.getDay())
    ? "Peak"
    : "Non-Peak";
}

function TrashIcon() {
  return (
    <svg
      width="17"
      height="19"
      viewBox="0 0 20 22"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 5.5h13M7.5 5.5V3h5v2.5M5 5.5l.8 14h8.4l.8-14M8 8.5v8M10 8.5v8M12 8.5v8"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckoutLoader() {
  const overlayRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    const padding =
      parseFloat(getComputedStyle(document.body).paddingRight) || 0;

    const elements = [];

    Array.from(document.body.children).forEach((element) => {
      if (
        !(element instanceof HTMLElement) ||
        element === overlayRef.current
      ) {
        return;
      }

      elements.push({
        element,
        inert: element.inert,
      });

      element.inert = true;
    });

    document.body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight =
        `${padding + scrollbarWidth}px`;
    }

    overlayRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;

      elements.forEach(({ element, inert }) => {
        element.inert = inert;
      });

      if (previousFocus?.isConnected) {
        previousFocus.focus?.();
      }
    };
  }, []);

  return createPortal(
    <div
      ref={overlayRef}
      className="cf-loading-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Preparing payment"
      aria-busy="true"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Tab") {
          event.preventDefault();
        }
      }}
    >
      <div className="cf-loading-dots" role="status">
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />

        <span className="cf-sr-only">
          Preparing your payment. Please wait.
        </span>
      </div>
    </div>,
    document.body
  );
}

export default function Form({
  nextPath = "/payment",
  dressImage = "/dress.png",
  privacyBmPath = "/privacy-policy-bm",
  privacyEnPath = "/privacy-policy",
  onProceed,
  onApplyPromo,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const formRef = useRef(null);
  const timerRef = useRef(null);
  const submittingRef = useRef(false);
  const mountedRef = useRef(false);
  const termsId = useId();

  const [booking] = useState(
    () => location.state?.booking || readStored("visitBooking")
  );

  const [tickets, setTickets] = useState(() => {
    const stored = readStored("selectedTickets");

    return normalizeTickets(
      Array.isArray(stored)
        ? stored
        : location.state?.selectedTickets ||
            location.state?.tickets ||
            booking?.tickets ||
            []
    );
  });

  const [contact, setContact] = useState(() => {
    const saved = readStored("checkoutContact", sessionStorage);

    return {
      email: saved?.email || "",
      name: saved?.name || "",
      phoneCountry: saved?.phoneCountry || "MY",
      phone: saved?.phone || "",
      nationality: saved?.nationality || "",
    };
  });

  const [agreed, setAgreed] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMessage, setPromoMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const countries = useMemo(
    () =>
      [...nationalities].sort((a, b) =>
        a.name.localeCompare(b.name, "en")
      ),
    []
  );

  const totalQuantity = tickets.reduce(
    (sum, ticket) => sum + ticket.quantity,
    0
  );

  const subtotalCents = tickets.reduce(
    (sum, ticket) =>
      sum + Math.round(ticket.price * 100) * ticket.quantity,
    0
  );

  const discountCents = Math.min(
    subtotalCents,
    Math.max(0, Math.round((promo?.discount || 0) * 100))
  );

  const total = (subtotalCents - discountCents) / 100;

  const phoneCountry =
    DIAL_CODES.find(
      (country) => country.code === contact.phoneCountry
    ) || DIAL_CODES[0];

  function updateContact(event) {
    if (submittingRef.current) return;

    const { name, value } = event.target;

    setContact((current) => ({
      ...current,
      [name]: value,
    }));

    setError("");
  }

  function clearCart() {
    if (submittingRef.current || promoBusy) return;

    try {
      localStorage.setItem("selectedTickets", "[]");
      localStorage.removeItem("bookingCheckout");
    } catch {
      setError("Unable to update your cart. Please try again.");
      return;
    }

    setTickets([]);
    setPromo(null);
    setPromoMessage("");

    window.dispatchEvent(new Event("cartUpdated"));
  }

  async function applyPromo() {
    if (submittingRef.current || promoBusy) return;

    const code = promoCode.trim();

    if (!code) {
      setPromoMessage("Please enter a promo code.");
      return;
    }

    if (!tickets.length) {
      setPromoMessage("Your cart is empty.");
      return;
    }

    if (!onApplyPromo) {
      setPromoMessage("This promo code could not be applied.");
      return;
    }

    setPromoBusy(true);
    setPromoMessage("");
    setPromo(null);

    try {
      const result = await onApplyPromo({
        code,
        tickets,
        subtotal: subtotalCents / 100,
      });

      if (!mountedRef.current) return;

      const discount = Number(result?.discount);

      if (
        !result?.valid ||
        !Number.isFinite(discount) ||
        discount < 0
      ) {
        setPromoMessage(result?.message || "Invalid promo code.");
        return;
      }

      setPromo({
        code,
        discount: Math.min(subtotalCents / 100, discount),
      });

      setPromoMessage("Promo code applied.");
    } catch {
      if (mountedRef.current) {
        setPromoMessage(
          "Unable to apply the promo code. Please try again."
        );
      }
    } finally {
      if (mountedRef.current) {
        setPromoBusy(false);
      }
    }
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (submittingRef.current || promoBusy) return;

    if (!tickets.length) {
      setError("Your cart is empty. Please add tickets first.");
      return;
    }

    if (!formRef.current?.reportValidity()) return;

    const name = contact.name.trim();
    const email = contact.email.trim();
    const phoneDigits = contact.phone.replace(/\D/g, "");

    if (!name) {
      setError("Please enter your name.");
      return;
    }

    if (phoneDigits.length < 6 || phoneDigits.length > 14) {
      setError("Please enter a valid phone number.");
      return;
    }

    if (!agreed) {
      setError("Please agree to the Terms and Conditions.");
      return;
    }

    const contactData = {
      ...contact,
      name,
      email,
      phone: contact.phone.trim(),
      dialCode: phoneCountry.dial,
      internationalPhone: `${phoneCountry.dial}${phoneDigits}`,
    };

    const checkout = {
      tickets,
      contact: contactData,
      subtotal: subtotalCents / 100,
      discount: discountCents / 100,
      promoCode: promo?.code || null,
      total,
      totalQuantity,
      currency: "MYR",
      termsAccepted: true,
      termsAcceptedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(
        "selectedTickets",
        JSON.stringify(tickets)
      );

      sessionStorage.setItem(
        "checkoutContact",
        JSON.stringify(contactData)
      );

      sessionStorage.setItem(
        "checkoutDetails",
        JSON.stringify(checkout)
      );

      localStorage.setItem(
        "bookingCheckout",
        JSON.stringify({
          tickets,
          subtotal: checkout.subtotal,
          discount: checkout.discount,
          promoCode: checkout.promoCode,
          total,
          totalQuantity,
          currency: "MYR",
        })
      );
    } catch {
      setError("Unable to save your details. Please try again.");
      return;
    }

    const state = {
      booking,
      checkout,
      contact: contactData,
      tickets,
      selectedTickets: tickets,
    };

    submittingRef.current = true;
    setError("");
    setLoading(true);

    timerRef.current = window.setTimeout(async () => {
      timerRef.current = null;

      if (!mountedRef.current) return;

      try {
        if (onProceed) {
          await onProceed(state);

          if (mountedRef.current) {
            submittingRef.current = false;
            setLoading(false);
          }

          return;
        }

        navigate(nextPath, { state });

        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "instant",
        });
      } catch {
        if (mountedRef.current) {
          submittingRef.current = false;
          setLoading(false);
          setError("Unable to continue. Please try again.");
        }
      }
    }, 2000);
  }

  return (
    <main className="cf-page" aria-busy={loading}>
      <style>{CSS}</style>

      <form
        ref={formRef}
        className="cf-container"
        onSubmit={handleSubmit}
      >
        <div className="cf-topbar">
          <h1>Checkout</h1>

          <Link to="/" className="cf-add-tickets">
            Add More Tickets
          </Link>
        </div>

        <div className="cf-columns">
          <div className="cf-left">
            <section
              className="cf-card cf-notes"
              aria-labelledby={termsId}
            >
              <h2 className="cf-card-heading" id={termsId}>
                Things to note
              </h2>

           <div className="cf-notes-body">
  <h3>TERMS &amp; CONDITIONS</h3>

  <ol className="cf-terms">
    <li>
      Admission of children under aged 3 is FREE of charge.
    </li>

    <li>
      Tickets sold are not refundable or transferable.
    </li>

    <li>
      An adult MUST accompany children below 12 years of age.
    </li>

    <li>
      Eating, drinking, chewing gum and smoking are not allowed
      during the visit.
    </li>

    <li>
      The management reserves the right to reschedule or cancel
      visits without prior notice.
    </li>

    <li>
      The management reserves the right to refuse any entry to
      any visitor(s).
    </li>

    <li>
      Visitors shall at all times, adhere to and obey:

      <ul>
        <li>
          The signs and notices displayed in, on and/or around
          the premises;
        </li>

        <li>
          The rules and regulations of the Petrosains, The
          Discovery Centre; and
        </li>

        <li>
          The instructions of the officers in the Petrosains,
          The Discovery Centre Operations.
        </li>
      </ul>
    </li>

    <li>
      The management may remove any visitor who is behaving in
      an indecent, unruly or disruptive manner or poses any kind
      of danger or discomfort to other visitors.
    </li>
  </ol>
</div>
            </section>

            <section
              className="cf-card cf-contact"
              aria-labelledby="cf-contact-heading"
            >
              <h2
                className="cf-card-heading"
                id="cf-contact-heading"
              >
                Contact Information
              </h2>

              <div className="cf-contact-body">
                <label className="cf-field">
                  <span className="cf-sr-only">Email</span>

                  <input
                    type="email"
                    name="email"
                    placeholder="Email*"
                    autoComplete="email"
                    required
                    disabled={loading}
                    value={contact.email}
                    onChange={updateContact}
                    aria-describedby="cf-email-help"
                  />
                </label>

                <p className="cf-email-help" id="cf-email-help">
                  <span aria-hidden="true">?</span>
                  Please provide a valid email to receive your
                  e-tickets.
                </p>

                <label className="cf-field cf-name-field">
                  <span className="cf-sr-only">Name</span>

                  <input
                    type="text"
                    name="name"
                    placeholder="Name*"
                    autoComplete="name"
                    required
                    disabled={loading}
                    value={contact.name}
                    onChange={updateContact}
                  />
                </label>

                <div className="cf-phone">
                  <div className="cf-dial">
                    <span aria-hidden="true">
                      {flag(phoneCountry.code)}
                    </span>

                    <span aria-hidden="true">
                      {phoneCountry.dial}
                    </span>

                    <span className="cf-small-arrow" />

                    <select
                      name="phoneCountry"
                      aria-label="Phone country code"
                      disabled={loading}
                      value={contact.phoneCountry}
                      onChange={updateContact}
                    >
                      {DIAL_CODES.map((country) => (
                        <option
                          key={country.code}
                          value={country.code}
                        >
                          {flag(country.code)} {country.code}{" "}
                          {country.dial}
                        </option>
                      ))}
                    </select>
                  </div>

                  <label className="cf-phone-number">
                    <span>Phone Number*</span>

                    <input
                      type="tel"
                      name="phone"
                      autoComplete="tel-national"
                      inputMode="tel"
                      placeholder="12-345 6789"
                      required
                      maxLength={24}
                      disabled={loading}
                      value={contact.phone}
                      onChange={updateContact}
                    />
                  </label>
                </div>

                <div className="cf-nationality">
                  <label htmlFor="cf-nationality-select">
                    Nationality*
                  </label>

                  <div className="cf-select-wrap">
                    <select
                      id="cf-nationality-select"
                      name="nationality"
                      required
                      disabled={loading}
                      value={contact.nationality}
                      onChange={updateContact}
                      className={
                        contact.nationality ? "has-value" : ""
                      }
                    >
                      <option value="" disabled>
                        Please select
                      </option>

                      {countries.map((country) => (
                        <option
                          key={country.code}
                          value={country.code}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>

                    <span className="cf-select-arrow" />
                  </div>
                </div>

                <label className="cf-agreement">
                  <input
                    type="checkbox"
                    required
                    disabled={loading}
                    checked={agreed}
                    onChange={(event) => {
                      setAgreed(event.target.checked);
                      setError("");
                    }}
                  />

                 <span>
  I have read and agree to the Terms and Conditions &amp;{" "}
  <a
    href="https://www.petronas.com/bm/privacy-statement"
    target="_blank"
    rel="noopener noreferrer"
  >
    Petrosains Privacy Policy (BM)
  </a>{" "}
  /{" "}
  <a
    href="https://www.petronas.com/privacy-statement"
    target="_blank"
    rel="noopener noreferrer"
  >
    Petrosains Privacy Policy (EN)
  </a>
  *
</span>
                </label>

                {error && (
                  <p className="cf-error" role="alert">
                    {error}
                  </p>
                )}
              </div>
            </section>
          </div>

          <aside className="cf-right">
            <section className="cf-card cf-cart">
              <div className="cf-cart-heading">
                <h2>My Cart ({totalQuantity})</h2>

                <button
                  type="button"
                  className="cf-trash"
                  aria-label="Remove all tickets"
                  disabled={
                    !tickets.length || promoBusy || loading
                  }
                  onClick={clearCart}
                >
                  <TrashIcon />
                </button>
              </div>

              <div className="cf-cart-items">
                {tickets.length ? (
                  tickets.map((ticket) => {
                    const product =
                      PRODUCT_NAMES[ticket.attractionId] ||
                      ticket.title ||
                      "Sky Xperience";

                    const resident =
                      (ticket.residentType ||
                        booking?.residentType) === "mykad"
                        ? "MyKad"
                        : "Non MyKad";

                    const status = ticketStatus(ticket, booking);

                    const type = (
                      ticket.ticketLabel ||
                      ticket.ticketType ||
                      "Adult"
                    ).toUpperCase();

                    const visitDate =
                      ticket.visitDate || booking?.visitDate;

                    const time =
                      ticket.timeSlot || booking?.timeSlot;

                    const scienceTime =
                      ticket.scienceCentreTimeSlot ||
                      booking?.scienceCentreTimeSlot;

                    const scienceDate =
                      ticket.scienceCentreDate ||
                      booking?.scienceCentreDate ||
                      visitDate;

                    const visitTitle =
                      ticket.attractionId ===
                      "petrosains-double-deal"
                        ? "PETRONAS Twin Towers Sky Xperience + Petrosains"
                        : product;

                    return (
                      <article
                        className="cf-cart-item"
                        key={ticket.id}
                      >
                        <h3>
                          {product} – {resident} ({status})
                        </h3>

                        <p className="cf-ticket-quantity">
                          {ticket.quantity} X {type}
                        </p>

                        {ticket.attractionId ===
                          "petrosains-double-deal" &&
                          scienceTime && (
                            <p className="cf-session">
                              <strong>
                                Science Centre – {resident} (
                                {status}) ({type}):
                              </strong>{" "}
                              {formatSession(
                                scienceDate,
                                scienceTime
                              )}
                            </p>
                          )}

                        <p className="cf-session">
                          <strong>
                            {visitTitle} – {resident} ({status}) (
                            {type}):
                          </strong>{" "}
                          {formatSession(visitDate, time)}
                        </p>
                      </article>
                    );
                  })
                ) : (
                  <p className="cf-empty">Your cart is empty.</p>
                )}
              </div>

              <div className="cf-promo">
             
             

                {promoMessage && (
                  <p
                    className={`cf-promo-message ${
                      promo ? "is-success" : ""
                    }`}
                    role="status"
                  >
                    {promoMessage}
                  </p>
                )}
              </div>

              <div className="cf-total">
                <strong>Total</strong>
                <strong>MYR {money(total)}</strong>
              </div>
            </section>

            <div className="cf-actions">
              <button
                type="button"
                className="cf-back"
                disabled={loading}
                onClick={() => navigate("/booking")}
              >
                Back
              </button>

              <button
                type="submit"
                className="cf-proceed"
                disabled={!tickets.length || promoBusy || loading}
              >
                {loading ? "Please wait..." : "Proceed To Pay"}
              </button>
            </div>
          </aside>
        </div>
      </form>

      {loading && <CheckoutLoader />}
    </main>
  );
}

const CSS = `
.cf-page {
  padding: 16px 24px 40px;
  background: #fff;
  color: #050505;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.5;
}

.cf-page *,
.cf-loading-overlay * {
  box-sizing: border-box;
}

.cf-page input,
.cf-page select,
.cf-page button {
  font: inherit;
}

.cf-page button { cursor: pointer; }

.cf-page button:focus-visible,
.cf-page a:focus-visible {
  outline: 3px solid #007d69;
  outline-offset: 3px;
}

.cf-container {
  width: 100%;
  max-width: 1248px;
  margin: 0 auto;
}

.cf-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 42px;
  margin-bottom: 26px;
}

.cf-topbar h1 {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 42px;
  letter-spacing: -.4px;
}

.cf-add-tickets {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 41px;
  padding: 0 14px;
  border: 2px solid #b0b0b0;
  border-radius: 6px;
  background: #fff;
  color: #aaa;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
}

.cf-columns {
  display: grid;
  grid-template-columns: minmax(0, 714fr) minmax(0, 500fr);
  align-items: start;
  gap: 34px;
}

.cf-left, .cf-right { min-width: 0; }

.cf-card {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px #0000000d, 0 5px 29px #00000012;
}

.cf-card-heading {
  display: flex;
  align-items: center;
  min-height: 51px;
  margin: 0;
  padding: 0 22px;
  border-bottom: 1px solid #ddd;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.cf-notes-body { padding: 27px 27px 19px; }

.cf-notes-body > h3 {
  margin: 0 0 23px;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
}

.cf-terms {
  margin: 0;
  padding-left: 45px;
  list-style: decimal;
  font-size: 13px;
  line-height: 19px;
}

.cf-terms > li { padding-left: 0; }

.cf-terms ul {
  margin: 0;
  padding-left: 45px;
  list-style: circle;
}

.cf-dress {
  display: block;
  width: 338px;
  max-width: 100%;
  height: auto;
  margin: 24px auto 10px;
}

.cf-dress-notice {
  margin: 0;
  text-align: center;
  color: #ff0000;
  font-size: 12px;
  font-weight: 600;
  line-height: 19px;
}

.cf-contact { margin-top: 18px; }
.cf-contact-body { padding: 27px 22px 20px; }
.cf-field { display: block; }

.cf-field input {
  display: block;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  border: 1px solid #cbd2dc;
  border-radius: 4px;
  background: white;
  color: #050505;
  font-size: 15px;
  outline: none;
}

.cf-field input::placeholder {
  color: #050505;
  opacity: 1;
}

.cf-field input:focus,
.cf-phone:focus-within,
.cf-select-wrap select:focus,
.cf-promo-input:focus-within {
  border-color: #00ab91;
  box-shadow: 0 0 0 2px #00ab9115;
}

.cf-email-help {
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 16px 0 18px;
  font-size: 12px;
  line-height: 21px;
}

.cf-email-help > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #00ab91;
  color: white;
  font-family: Arial, sans-serif;
  font-size: 9px;
  font-weight: 700;
}

.cf-name-field { margin-bottom: 18px; }

.cf-phone {
  display: flex;
  height: 56px;
  overflow: hidden;
  border: 1px solid #cbd2dc;
  border-radius: 4px;
  background: white;
}

.cf-dial {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 87px;
  padding: 0 7px;
  background: #f1f1f1;
  font-size: 15px;
}

.cf-dial > span:first-child { font-size: 19px; }

.cf-dial select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.cf-small-arrow {
  width: 0;
  height: 0;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  border-top: 4px solid #555;
}

.cf-phone-number {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
  padding: 4px 9px;
}

.cf-phone-number > span {
  font-size: 13px;
  font-weight: 700;
  line-height: 19px;
}

.cf-phone-number input {
  width: 100%;
  min-width: 0;
  height: 24px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #050505;
  font-size: 18px;
  line-height: 24px;
}

.cf-phone-number input::placeholder {
  color: #b6bbc2;
  opacity: 1;
}

.cf-nationality {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 18px;
  margin-top: 18px;
}

.cf-nationality > label {
  padding-left: 9px;
  font-size: 15px;
}

.cf-select-wrap { position: relative; }

.cf-select-wrap select {
  width: 100%;
  height: 57px;
  padding: 0 37px 0 16px;
  border: 1px solid #cbd2dc;
  border-radius: 4px;
  appearance: none;
  background: white;
  color: #00ab91;
  font-size: 15px;
  outline: none;
  cursor: pointer;
}

.cf-select-wrap select.has-value,
.cf-select-wrap option {
  color: #050505;
}

.cf-select-arrow {
  position: absolute;
  top: 50%;
  right: 25px;
  pointer-events: none;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #888;
  transform: translateY(-50%);
}

.cf-agreement {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 27px;
  padding-left: 4px;
  font-size: 12px;
  line-height: 23px;
  cursor: pointer;
}

.cf-agreement input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 3px 0 0;
  accent-color: #00ab91;
  cursor: pointer;
}

.cf-agreement a {
  color: #00b1b1;
  text-decoration: none;
}

.cf-agreement a:hover { text-decoration: underline; }

.cf-cart-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 51px;
  padding: 0 18px 0 22px;
  border-bottom: 1px solid #ddd;
}

.cf-cart-heading h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.cf-trash {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: none;
  color: #00b3b1;
}

.cf-trash:hover:not(:disabled) { background: #effaf8; }
.cf-trash:disabled { opacity: .45; cursor: default; }
.cf-cart-items { padding: 24px 26px 25px; }

.cf-cart-item + .cf-cart-item {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.cf-cart-item h3 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 700;
  line-height: 25px;
}

.cf-ticket-quantity {
  margin: 0 0 5px;
  font-size: 15px;
  line-height: 24px;
}

.cf-session {
  margin: 4px 0 0;
  font-size: 15px;
  line-height: 24px;
}

.cf-session strong {
  font-size: 16px;
  font-weight: 600;
}

.cf-promo {
  padding: 16px 18px 18px;
  border-top: 1px solid #e1e1e1;
}

.cf-promo > label {
  display: block;
  margin-bottom: 18px;
  font-size: 16px;
  font-weight: 600;
  line-height: 25px;
}

.cf-promo-input {
  display: flex;
  height: 56px;
  border: 1px solid #cbd2dc;
  border-radius: 4px;
}

.cf-promo-input input {
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 0 13px;
  border: 0;
  border-radius: 4px 0 0 4px;
  outline: none;
  color: #050505;
  background: white;
  font-size: 15px;
}

.cf-promo-input input::placeholder {
  color: #050505;
  opacity: 1;
}

.cf-promo-input button {
  flex-shrink: 0;
  width: 75px;
  margin: -1px;
  border: 0;
  border-radius: 0 5px 5px 0;
  background: #00ab91;
  color: white;
  font-size: 15px;
  font-weight: 700;
}

.cf-promo-input button:disabled {
  opacity: .65;
  cursor: wait;
}

.cf-promo-message {
  margin: 9px 0 0;
  color: #b13b3b;
  font-size: 12px;
  line-height: 19px;
}

.cf-promo-message.is-success { color: #008974; }

.cf-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 80px;
  padding: 22px 26px;
  border-top: 1px solid #e1e1e1;
  font-size: 18px;
  line-height: 27px;
}

.cf-total strong { font-weight: 700; }
.cf-total strong:last-child { white-space: nowrap; }

.cf-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 34px;
  margin-top: 18px;
}

.cf-actions button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 51px;
  padding: 0 13px;
  border: 0;
  border-radius: 6px;
  color: white;
  font-size: 16px;
  font-weight: 700;
}

.cf-back { background: #f5cc48; }
.cf-back:hover:not(:disabled) { background: #eec13b; }
.cf-proceed { background: #00ab91; }
.cf-proceed:hover:not(:disabled) { background: #009b83; }
.cf-proceed:disabled { opacity: .45; cursor: not-allowed; }

.cf-page[aria-busy="true"] .cf-proceed {
  opacity: 1;
  cursor: wait;
}

.cf-error {
  margin: 15px 0 0;
  color: #c53333;
  font-size: 13px;
}

.cf-empty {
  margin: 0;
  color: #666;
  font-size: 15px;
}

.cf-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Загрузка перед переходом */
.cf-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, .72);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  outline: none;
  cursor: wait;
  touch-action: none;
  animation: cf-overlay-in 280ms ease both;
}

.cf-loading-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 76px;
  height: 40px;
}

.cf-loading-dots > span:not(.cf-sr-only) {
  display: block;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #00ab91;
  animation: cf-dot-pulse 900ms ease-in-out infinite;
}

.cf-loading-dots > span:nth-child(2) {
  animation-delay: 150ms;
}

.cf-loading-dots > span:nth-child(3) {
  animation-delay: 300ms;
}

@keyframes cf-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes cf-dot-pulse {
  0%, 80%, 100% {
    transform: scale(.5);
    opacity: .5;
  }

  40% {
    transform: scale(1.08);
    opacity: 1;
  }
}

/* Компактные размеры на ПК */
@media (min-width: 1024px) {
  .cf-page {
    padding: 14px 24px 34px;
    font-size: 14px;
  }

  .cf-container { max-width: 1120px; }

  .cf-topbar {
    gap: 16px;
    min-height: 36px;
    margin-bottom: 22px;
  }

  .cf-topbar h1 {
    font-size: 26px;
    line-height: 36px;
    letter-spacing: -.3px;
  }

  .cf-add-tickets {
    height: 36px;
    padding: 0 12px;
    font-size: 14px;
  }

  .cf-columns { gap: 28px; }

  .cf-card-heading {
    min-height: 44px;
    padding: 0 19px;
    font-size: 14px;
    line-height: 22px;
  }

  .cf-notes-body { padding: 22px 23px 16px; }

  .cf-notes-body > h3 {
    margin-bottom: 18px;
    font-size: 11px;
    line-height: 18px;
  }

  .cf-terms {
    padding-left: 34px;
    font-size: 12px;
    line-height: 18px;
  }

  .cf-terms ul { padding-left: 32px; }

  .cf-dress {
    width: 300px;
    margin: 20px auto 9px;
  }

  .cf-dress-notice {
    font-size: 11px;
    line-height: 18px;
  }

  .cf-contact { margin-top: 16px; }
  .cf-contact-body { padding: 22px 19px 18px; }

  .cf-field input {
    height: 48px;
    padding: 0 12px;
    font-size: 14px;
  }

  .cf-email-help {
    margin: 12px 0 15px;
    font-size: 11px;
    line-height: 19px;
  }

  .cf-name-field { margin-bottom: 15px; }
  .cf-phone { height: 48px; }

  .cf-dial {
    min-width: 80px;
    gap: 5px;
    font-size: 14px;
  }

  .cf-dial > span:first-child { font-size: 17px; }
  .cf-phone-number { padding: 3px 8px; }

  .cf-phone-number > span {
    font-size: 12px;
    line-height: 17px;
  }

  .cf-phone-number input {
    height: 22px;
    font-size: 16px;
    line-height: 22px;
  }

  .cf-nationality {
    gap: 15px;
    margin-top: 15px;
  }

  .cf-nationality > label {
    padding-left: 7px;
    font-size: 14px;
  }

  .cf-select-wrap select {
    height: 48px;
    padding: 0 32px 0 13px;
    font-size: 14px;
  }

  .cf-select-arrow { right: 20px; }

  .cf-agreement {
    gap: 8px;
    margin-top: 22px;
    padding-left: 3px;
    font-size: 11px;
    line-height: 21px;
  }

  .cf-agreement input {
    width: 16px;
    height: 16px;
    margin-top: 3px;
  }

  .cf-cart-heading {
    min-height: 44px;
    padding: 0 15px 0 19px;
  }

  .cf-cart-heading h2 {
    font-size: 14px;
    line-height: 22px;
  }

  .cf-trash { width: 24px; height: 28px; }
  .cf-trash svg { width: 15px; height: 17px; }
  .cf-cart-items { padding: 20px 22px 21px; }

  .cf-cart-item + .cf-cart-item {
    margin-top: 17px;
    padding-top: 17px;
  }

  .cf-cart-item h3 {
    margin-bottom: 10px;
    font-size: 14px;
    line-height: 22px;
  }

  .cf-ticket-quantity {
    margin-bottom: 4px;
    font-size: 13px;
    line-height: 21px;
  }

  .cf-session {
    margin-top: 3px;
    font-size: 13px;
    line-height: 21px;
  }

  .cf-session strong { font-size: 14px; }
  .cf-promo { padding: 14px 16px 16px; }

  .cf-promo > label {
    margin-bottom: 14px;
    font-size: 14px;
    line-height: 22px;
  }

  .cf-promo-input { height: 48px; }

  .cf-promo-input input {
    padding: 0 11px;
    font-size: 14px;
  }

  .cf-promo-input button {
    width: 66px;
    font-size: 14px;
  }

  .cf-promo-message {
    margin-top: 8px;
    font-size: 11px;
    line-height: 18px;
  }

  .cf-total {
    gap: 16px;
    min-height: 68px;
    padding: 18px 22px;
    font-size: 16px;
    line-height: 24px;
  }

  .cf-actions {
    gap: 28px;
    margin-top: 16px;
  }

  .cf-actions button {
    height: 43px;
    padding: 0 11px;
    font-size: 14px;
  }

  .cf-error { margin-top: 12px; font-size: 12px; }
  .cf-empty { font-size: 14px; }
}

@media (max-width: 1000px) {
  .cf-columns {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    gap: 24px;
  }

  .cf-notes-body { padding: 24px 20px 20px; }
  .cf-terms { padding-left: 25px; }
  .cf-terms ul { padding-left: 24px; }
  .cf-cart-items { padding: 22px 20px; }
  .cf-actions { gap: 18px; }
}

@media (max-width: 760px) {
  .cf-page { padding: 18px 16px 36px; }
  .cf-topbar { gap: 12px; margin-bottom: 22px; }
  .cf-topbar h1 { font-size: 26px; line-height: 36px; }
  .cf-add-tickets { height: 38px; padding: 0 10px; font-size: 13px; }
  .cf-columns { grid-template-columns: minmax(0, 1fr); gap: 24px; }
  .cf-card-heading { padding: 0 18px; font-size: 15px; }
  .cf-notes-body { padding: 22px 18px 20px; }
  .cf-notes-body > h3 { margin-bottom: 18px; }
  .cf-terms { padding-left: 21px; font-size: 12px; line-height: 19px; }
  .cf-terms ul { padding-left: 22px; }
  .cf-dress { margin-top: 22px; }
  .cf-dress-notice { font-size: 11px; line-height: 18px; }
  .cf-contact-body { padding: 22px 18px; }
  .cf-email-help { align-items: flex-start; font-size: 11px; }
  .cf-email-help > span { margin-top: 4px; }

  .cf-nationality {
    grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr);
  }

  .cf-nationality > label { padding-left: 0; font-size: 14px; }
  .cf-agreement { padding-left: 0; font-size: 11px; line-height: 21px; }
  .cf-cart-items { padding: 22px; }
  .cf-cart-item h3 { font-size: 15px; }
  .cf-session, .cf-ticket-quantity { font-size: 14px; }
  .cf-session strong { font-size: 15px; }
  .cf-actions { gap: 18px; }
  .cf-actions button { font-size: 14px; }
}

@media (prefers-reduced-motion: reduce) {
  .cf-loading-overlay { animation: none; }

  .cf-loading-dots > span:not(.cf-sr-only) {
    animation: none;
    opacity: 1;
  }
}
`;